import { createSlice, createAction, PayloadAction } from '@reduxjs/toolkit';
import { globalReset } from '../actions';
import authService, {
    User,
    UserProfile,
    UserStats,
    UserPreferences,
    UserSession,
    LoginRequest,
    RegisterRequest,
    UpdateProfileRequest,
    UpdateUserProfileRequest,
    ChangePasswordRequest,
    ForceChangePasswordRequest,
    AuthResponse,
    SimpleRegisterRequest,
    RegisterResponse,
    ProfileCompletionRequest,
    ProfileCompletionResponse,
    UpdateUserRequest,
} from '@/services/authService';
import pickBy from 'lodash/pickBy';

// Saga Actions for Login
export const loginRequest = createAction<LoginRequest>('auth/loginRequest');
export const loginSuccess = createAction<AuthResponse>('auth/loginSuccess');
export const loginFailure = createAction<string>('auth/loginFailure');

// Saga Actions for Registration
export const registerRequest = createAction<SimpleRegisterRequest>('auth/registerRequest');
export const registerSuccess = createAction<RegisterResponse>('auth/registerSuccess');
export const registerFailure = createAction<string>('auth/registerFailure');

export const fullRegisterRequest = createAction<RegisterRequest>('auth/fullRegisterRequest');
export const fullRegisterSuccess = createAction<RegisterResponse>('auth/fullRegisterSuccess');
export const fullRegisterFailure = createAction<string>('auth/fullRegisterFailure');

// Saga Actions for Profile Completion
export const completeProfileRequest = createAction<ProfileCompletionRequest>('auth/completeProfileRequest');
export const completeProfileSuccess = createAction<ProfileCompletionResponse>('auth/completeProfileSuccess');
export const completeProfileFailure = createAction<string>('auth/completeProfileFailure');

export const checkProfileCompletionRequest = createAction('auth/checkProfileCompletionRequest');
export const checkProfileCompletionSuccess = createAction<{ profile_completed: boolean; user: User }>(
    'auth/checkProfileCompletionSuccess',
);
export const checkProfileCompletionFailure = createAction<string>('auth/checkProfileCompletionFailure');

export const getCurrentUserRequest = createAction('auth/getCurrentUserRequest');
export const getCurrentUserSuccess = createAction<User>('auth/getCurrentUserSuccess');
export const getCurrentUserFailure = createAction<string>('auth/getCurrentUserFailure');

export const logoutRequest = createAction('auth/logoutRequest');
export const logoutSuccess = createAction('auth/logoutSuccess');
export const logoutFailure = createAction<string>('auth/logoutFailure');

export const updateUserRequest = createAction<UpdateUserRequest>('auth/updateUserRequest');
export const updateUserSuccess = createAction<User>('auth/updateUserSuccess');
export const updateUserFailure = createAction<string>('auth/updateUserFailure');

export const updateProfileRequest = createAction<UpdateProfileRequest>('auth/updateProfileRequest');
export const updateProfileSuccess = createAction<User>('auth/updateProfileSuccess');
export const updateProfileFailure = createAction<string>('auth/updateProfileFailure');

export const fetchUserProfileRequest = createAction('auth/fetchUserProfileRequest');
export const fetchUserProfileSuccess = createAction<UserProfile>('auth/fetchUserProfileSuccess');
export const fetchUserProfileFailure = createAction<string>('auth/fetchUserProfileFailure');

export const updateUserProfileRequest = createAction<UpdateUserProfileRequest>('auth/updateUserProfileRequest');
export const updateUserProfileSuccess = createAction<UserProfile>('auth/updateUserProfileSuccess');
export const updateUserProfileFailure = createAction<string>('auth/updateUserProfileFailure');

export const changePasswordRequest = createAction<ChangePasswordRequest>('auth/changePasswordRequest');
export const changePasswordSuccess = createAction<string>('auth/changePasswordSuccess');
export const changePasswordFailure = createAction<string>('auth/changePasswordFailure');
export const clearPasswordChangeSuccess = createAction('auth/clearPasswordChangeSuccess');

export const forceChangePasswordRequest = createAction<ForceChangePasswordRequest>('auth/forceChangePasswordRequest');
export const forceChangePasswordSuccess = createAction('auth/forceChangePasswordSuccess');
export const forceChangePasswordFailure = createAction<string>('auth/forceChangePasswordFailure');
export const setMustChangePassword = createAction<boolean>('auth/setMustChangePassword');

export const fetchUserStatsRequest = createAction('auth/fetchUserStatsRequest');
export const fetchUserStatsSuccess = createAction<UserStats>('auth/fetchUserStatsSuccess');
export const fetchUserStatsFailure = createAction<string>('auth/fetchUserStatsFailure');

export const fetchUserPreferencesRequest = createAction('auth/fetchUserPreferencesRequest');
export const fetchUserPreferencesSuccess = createAction<UserPreferences>('auth/fetchUserPreferencesSuccess');
export const fetchUserPreferencesFailure = createAction<string>('auth/fetchUserPreferencesFailure');

export const updateUserPreferencesRequest = createAction<Partial<UserPreferences>>('auth/updateUserPreferencesRequest');
export const updateUserPreferencesSuccess = createAction<Partial<UserPreferences>>('auth/updateUserPreferencesSuccess');
export const updateUserPreferencesFailure = createAction<string>('auth/updateUserPreferencesFailure');

export const startLearningSessionRequest = createAction<UserSession['session_type']>(
    'auth/startLearningSessionRequest',
);
export const startLearningSessionSuccess = createAction<UserSession>('auth/startLearningSessionSuccess');
export const startLearningSessionFailure = createAction<string>('auth/startLearningSessionFailure');

export const endLearningSessionRequest = createAction<{
    session_id: string;
    duration_minutes: number;
    activities_completed: number;
    xp_earned: number;
}>('auth/endLearningSessionRequest');
export const endLearningSessionSuccess = createAction<UserSession>('auth/endLearningSessionSuccess');
export const endLearningSessionFailure = createAction<string>('auth/endLearningSessionFailure');

export const forgotPasswordRequest = createAction<string>('auth/forgotPasswordRequest');
export const forgotPasswordSuccess = createAction('auth/forgotPasswordSuccess');
export const forgotPasswordFailure = createAction<string>('auth/forgotPasswordFailure');

export const resetPasswordRequest = createAction<{ token: string; newPassword: string }>('auth/resetPasswordRequest');
export const resetPasswordSuccess = createAction('auth/resetPasswordSuccess');
export const resetPasswordFailure = createAction<string>('auth/resetPasswordFailure');

// Add new action for initializing auth from persisted state
export const initializeAuthFromToken = createAction('auth/initializeAuthFromToken');

// Token refresh actions
export const refreshTokenRequest = createAction('auth/refreshTokenRequest');
export const refreshTokenSuccess = createAction('auth/refreshTokenSuccess');
export const refreshTokenFailure = createAction<string>('auth/refreshTokenFailure');

interface AuthState {
    user: User | null;
    profile?: UserProfile | null;
    stats: UserStats | null;
    preferences: UserPreferences | null;
    currentSession: UserSession | null;
    isAuthenticated: boolean;
    /** A cookie session may exist and /auth/user/ has not answered yet (EAA-2090). */
    isInitializing: boolean;
    isLoading: boolean;
    error: string | null;
    registrationSuccess: boolean;
    profileCompleted: boolean;
    mustChangePassword: boolean;
    isInitialAssessmentCompleted: boolean;
    hiddenPopupSurvey: boolean;
    avatar: string | null;
    passwordChangeSuccess: string | null;
}

const initialState: AuthState = {
    user: null,
    profile: null,
    stats: null,
    preferences: null,
    currentSession: null,
    isAuthenticated: false,
    isInitializing: authService.hasSession(),
    isLoading: false,
    error: null,
    registrationSuccess: false,
    profileCompleted: false,
    mustChangePassword: false,
    isInitialAssessmentCompleted: false,
    hiddenPopupSurvey: false,
    avatar: null,
    passwordChangeSuccess: null,
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearRegistrationSuccess: (state) => {
            state.registrationSuccess = false;
        },
        setProfileCompleted: (state, action: PayloadAction<boolean>) => {
            state.profileCompleted = action.payload;
        },
        setHiddenPopupSurvey: (state, action: PayloadAction<boolean>) => {
            state.hiddenPopupSurvey = action.payload;
        },
        setIsInitialAssessmentCompleted: (state, action: PayloadAction<boolean>) => {
            state.isInitialAssessmentCompleted = action.payload;
        },
        authInitDone: (state) => {
            state.isInitializing = false;
        },
        resetAuth: (state) => {
            state.user = null;
            state.profile = null;
            state.stats = null;
            state.preferences = null;
            state.currentSession = null;
            state.isAuthenticated = false;
            state.isInitializing = false;
            state.isLoading = false;
            state.error = null;
            state.registrationSuccess = false;
            state.profileCompleted = false;
            state.mustChangePassword = false;
            state.isInitialAssessmentCompleted = false;
            state.avatar = null;
            authService.clearSession();
        },
    },
    extraReducers: (builder) => {
        builder
            // Login
            .addCase(loginRequest, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(loginSuccess, (state, action) => {
                state.isLoading = false;
                state.isAuthenticated = true;
                state.user = action.payload.user;
                state.profileCompleted = action.payload.profile_completed;
                state.hiddenPopupSurvey = action.payload.profile_completed;
                state.isInitialAssessmentCompleted = action.payload.is_initial_assessment_completed;
                state.mustChangePassword = Boolean(action.payload.must_change_password);
                state.error = null;
            })
            .addCase(loginFailure, (state, action) => {
                state.isLoading = false;
                state.isAuthenticated = false;
                state.user = null;
                state.error = action.payload;
            })

            // Simple Registration
            .addCase(registerRequest, (state) => {
                state.isLoading = true;
                state.error = null;
                state.registrationSuccess = false;
            })
            .addCase(registerSuccess, (state, action) => {
                state.isLoading = false;
                state.registrationSuccess = true;
                state.profileCompleted = action.payload.profile_completed;
                state.error = null;
            })
            .addCase(registerFailure, (state, action) => {
                state.isLoading = false;
                state.registrationSuccess = false;
                state.error = action.payload;
            })

            // Full Registration (deprecated)
            .addCase(fullRegisterRequest, (state) => {
                state.isLoading = true;
                state.error = null;
                state.registrationSuccess = false;
            })
            .addCase(fullRegisterSuccess, (state, action) => {
                state.isLoading = false;
                state.registrationSuccess = true;
                state.user = action.payload.user;
                state.profileCompleted = action.payload.profile_completed;
                state.error = null;
            })
            .addCase(fullRegisterFailure, (state, action) => {
                state.isLoading = false;
                state.registrationSuccess = false;
                state.error = action.payload;
            })

            // Profile Completion
            .addCase(completeProfileRequest, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(completeProfileSuccess, (state, action) => {
                state.isLoading = false;
                state.user = action.payload.user;
                state.profileCompleted = action.payload.profile_completed;
                state.error = null;
            })
            .addCase(completeProfileFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Check Profile Completion
            .addCase(checkProfileCompletionRequest, (state) => {
                state.isLoading = true;
            })
            .addCase(checkProfileCompletionSuccess, (state, action) => {
                state.isLoading = false;
                state.user = action.payload.user;
                state.profileCompleted = action.payload.profile_completed;
            })
            .addCase(checkProfileCompletionFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Get Current User
            .addCase(getCurrentUserRequest, (state) => {
                state.isLoading = true;
            })
            .addCase(getCurrentUserSuccess, (state, action) => {
                state.isLoading = false;
                state.user = action.payload;
                state.profile = action.payload?.profile;
                state.mustChangePassword = Boolean(action.payload.must_change_password);

                if (action.payload.id) {
                    localStorage.setItem('user_id', `${action.payload.id}`);
                }
                state.isAuthenticated = true;
                state.isInitializing = false;
            })
            .addCase(getCurrentUserFailure, (state, action) => {
                state.isLoading = false;
                state.isAuthenticated = false;
                state.isInitializing = false;
                state.user = null;
                state.error = action.payload;
            })

            // Logout
            .addCase(logoutRequest, (state) => {
                state.isLoading = true;
            })
            .addCase(logoutSuccess, (state) => {
                state.isLoading = false;
                state.isAuthenticated = false;
                state.user = null;
                state.profileCompleted = false;
                state.mustChangePassword = false;
                state.error = null;
            })
            .addCase(logoutFailure, (state, action) => {
                state.isLoading = false;
                // Still clear auth state even if logout API call failed
                state.isAuthenticated = false;
                state.user = null;
                state.profileCompleted = false;
                state.mustChangePassword = false;
                state.error = action.payload;
            })

            // Profile Management
            .addCase(updateProfileRequest, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateProfileSuccess, (state, action) => {
                state.isLoading = false;
                state.user = action.payload;
                state.error = null;
            })
            .addCase(updateProfileFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // User Profile
            .addCase(fetchUserProfileRequest, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchUserProfileSuccess, (state, action) => {
                state.isLoading = false;
                state.profile = action.payload;
                state.error = null;
            })
            .addCase(fetchUserProfileFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Update User
            .addCase(updateUserRequest, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateUserSuccess, (state, action) => {
                state.isLoading = false;
                if (state.user) {
                    state.user.first_name = action.payload.first_name;
                    state.user.last_name = action.payload.last_name;
                    state.user.phone_number = action.payload.phone_number;
                    state.user.avatar_url = action.payload.avatar_url;
                }
                state.error = null;
            })
            .addCase(updateUserFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            .addCase(updateUserProfileRequest, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateUserProfileSuccess, (state, action) => {
                state.isLoading = false;
                state.profile = action.payload;
                state.error = null;
            })
            .addCase(updateUserProfileFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Password Change
            .addCase(changePasswordRequest, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(changePasswordSuccess, (state, action) => {
                state.isLoading = false;
                state.error = null;
                state.passwordChangeSuccess = action.payload;
            })
            .addCase(changePasswordFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(clearPasswordChangeSuccess, (state) => {
                state.isLoading = false;
                state.error = null;
                state.passwordChangeSuccess = null;
            })

            // Force password change
            .addCase(forceChangePasswordRequest, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(forceChangePasswordSuccess, (state) => {
                state.isLoading = false;
                state.mustChangePassword = false;
                state.error = null;
                if (state.user) {
                    state.user.must_change_password = false;
                }
            })
            .addCase(forceChangePasswordFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            .addCase(setMustChangePassword, (state, action) => {
                state.mustChangePassword = action.payload;
            })

            // User Stats
            .addCase(fetchUserStatsRequest, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchUserStatsSuccess, (state, action) => {
                state.isLoading = false;
                state.stats = action.payload;
                state.error = null;
            })
            .addCase(fetchUserStatsFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // User Preferences
            .addCase(fetchUserPreferencesRequest, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchUserPreferencesSuccess, (state, action) => {
                state.isLoading = false;
                state.preferences = action.payload;
                state.error = null;
            })
            .addCase(fetchUserPreferencesFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            .addCase(updateUserPreferencesRequest, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateUserPreferencesSuccess, (state, action) => {
                state.isLoading = false;
                if (state.preferences) {
                    state.preferences = { ...state.preferences, ...action.payload };
                }
                state.error = null;
            })
            .addCase(updateUserPreferencesFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Learning Sessions
            .addCase(startLearningSessionRequest, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(startLearningSessionSuccess, (state, action) => {
                state.isLoading = false;
                state.currentSession = action.payload;
                state.error = null;
            })
            .addCase(startLearningSessionFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            .addCase(endLearningSessionRequest, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(endLearningSessionSuccess, (state, action) => {
                state.isLoading = false;
                state.currentSession = null;
                state.error = null;
                // Update user XP if available
                if (state.user) {
                    state.user.xp_points += action.payload.xp_earned;
                }
            })
            .addCase(endLearningSessionFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Password Reset
            .addCase(forgotPasswordRequest, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(forgotPasswordSuccess, (state) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(forgotPasswordFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            .addCase(resetPasswordRequest, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(resetPasswordSuccess, (state) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(resetPasswordFailure, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // Token refresh
            .addCase(refreshTokenRequest, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(refreshTokenSuccess, (state) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(refreshTokenFailure, (state, action) => {
                state.isLoading = false;
                state.isAuthenticated = false;
                state.error = action.payload;
                authService.clearSession();
            })

            // Global reset action - clears all auth state
            .addCase(globalReset, (state) => {
                Object.assign(state, initialState);
                state.isInitializing = false;
                authService.clearSession();
            });
    },
});

export const {
    clearError,
    clearRegistrationSuccess,
    setProfileCompleted,
    setIsInitialAssessmentCompleted,
    setHiddenPopupSurvey,
    authInitDone,
    resetAuth,
} = authSlice.actions;

export default authSlice.reducer;
